import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import weekday from 'dayjs/plugin/weekday';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localeData from 'dayjs/plugin/localeData';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

// Extend Day.js with the plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(weekday);
dayjs.extend(customParseFormat);
dayjs.extend(localeData);
dayjs.extend(utc);
dayjs.extend(timezone);

const getLunchBreakForVenue = (venue) => {
  const venueName = venue?.venue;

  if (venueName === 'Acting Hall' || venueName === 'Modelling Hall') {
    return {
      breakStartTime: dayjs().hour(12).minute(0).second(0),
      breakEndTime: dayjs().hour(13).minute(0).second(0),
    };
  }

  if (venueName === 'Dance Hall' || venueName === 'Music Hall') {
    return {
      breakStartTime: dayjs().hour(12).minute(30).second(0),
      breakEndTime: dayjs().hour(13).minute(30).second(0),
    };
  }

  return null;
};

const overlapsBreak = (firstBreak, secondBreak) => {
  return (
    firstBreak.breakStartTime.isBefore(secondBreak.breakEndTime) &&
    firstBreak.breakEndTime.isAfter(secondBreak.breakStartTime)
  );
};

const generateRecurringBreaks = (timesForDate, lunchBreak) => {
  const dayStartTime = timesForDate?.startTime;
  const dayEndTime = timesForDate?.endTime;

  if (!dayStartTime || !dayEndTime) {
    return [];
  }

  const recurringBreaks = [];
  let breakStart = dayStartTime.clone().add(2, 'hour');

  while (breakStart.isBefore(dayEndTime)) {
    const breakEnd = breakStart.clone().add(15, 'minute');

    if (!breakEnd.isAfter(dayEndTime)) {
      const recurringBreak = {
        breakStartTime: breakStart,
        breakEndTime: breakEnd,
      };

      if (!lunchBreak || !overlapsBreak(recurringBreak, lunchBreak)) {
        recurringBreaks.push(recurringBreak);
      }
    }

    breakStart = breakStart.add(2, 'hour');
  }

  return recurringBreaks;
};

const getBreaksForVenue = (timesForDate, venue) => {
  const manualBreaks = timesForDate?.breaks || [];
  const lunchBreak = getLunchBreakForVenue(venue);
  const recurringBreaks = generateRecurringBreaks(timesForDate, lunchBreak);
  const combinedBreaks = [
    ...(lunchBreak ? [lunchBreak] : []),
    ...manualBreaks,
    ...recurringBreaks,
  ];

  const uniqueBreaks = new Map();
  combinedBreaks.forEach((breakItem) => {
    if (!breakItem?.breakStartTime || !breakItem?.breakEndTime) {
      return;
    }

    const key = `${breakItem.breakStartTime.format('HH:mm')}-${breakItem.breakEndTime.format('HH:mm')}`;
    if (!uniqueBreaks.has(key)) {
      uniqueBreaks.set(key, breakItem);
    }
  });

  return Array.from(uniqueBreaks.values()).sort(
    (a, b) => a.breakStartTime.valueOf() - b.breakStartTime.valueOf()
  );
};

// Estimate an endTime given a startTime and required available minutes
const estimateEndTimeForRequiredMinutes = (startTime, requiredMinutes, venue) => {
  if (!startTime || !requiredMinutes || requiredMinutes <= 0) return null;

  // Start with a naive endTime equal to start + requiredMinutes
  let candidateEnd = startTime.clone().add(requiredMinutes, 'minute');

  // Cap loop to avoid infinite loops
  const maxIterations = 50;
  let iter = 0;

  while (iter < maxIterations) {
    iter++;

    // Build a timesForDate object to derive breaks
    const timesForDate = { startTime: startTime.clone(), endTime: candidateEnd.clone() };

    // Determine breaks that fall between start and candidateEnd
    const breaks = getBreaksForVenue(timesForDate, venue) || [];

    // Sum break minutes that overlap the [startTime, candidateEnd)
    let totalBreakMinutes = 0;
    breaks.forEach((b) => {
      // Map break times to the same date as startTime/candidateEnd
      const bStart = startTime.clone().hour(b.breakStartTime.hour()).minute(b.breakStartTime.minute()).second(0);
      const bEnd = startTime.clone().hour(b.breakEndTime.hour()).minute(b.breakEndTime.minute()).second(0);
      if (bEnd.isSameOrBefore(startTime) || bStart.isSameOrAfter(candidateEnd)) return;
      const overlapStart = bStart.isAfter(startTime) ? bStart : startTime;
      const overlapEnd = bEnd.isBefore(candidateEnd) ? bEnd : candidateEnd;
      const mins = Math.max(0, overlapEnd.diff(overlapStart, 'minute'));
      totalBreakMinutes += mins;
    });

    const availableMinutes = candidateEnd.diff(startTime, 'minute') - totalBreakMinutes;

    if (availableMinutes >= requiredMinutes) {
      return candidateEnd;
    }

    // Need to extend candidateEnd by the shortfall and loop again
    const shortfall = requiredMinutes - availableMinutes;
    candidateEnd = candidateEnd.add(shortfall, 'minute');
  }

  // If we didn't converge, return the last candidate
  return candidateEnd;
};

// Function to get available intervals for a date by excluding breaks
const getAvailableIntervalsForDate = (timesForDate, date, venue) => {
  const startTime = timesForDate?.startTime;
  const endTime = timesForDate?.endTime;
  const breaks = getBreaksForVenue(timesForDate, venue);

  if (startTime && endTime) {
    const startDateTime = date
      .clone()
      .hour(startTime.hour())
      .minute(startTime.minute())
      .second(0);

    const endDateTime = date
      .clone()
      .hour(endTime.hour())
      .minute(endTime.minute())
      .second(0);

    let intervals = [{ startTime: startDateTime, endTime: endDateTime }];

    breaks.forEach((breakItem) => {
      const breakStartTime = date
        .clone()
        .hour(breakItem.breakStartTime.hour())
        .minute(breakItem.breakStartTime.minute())
        .second(0);

      const breakEndTime = date
        .clone()
        .hour(breakItem.breakEndTime.hour())
        .minute(breakItem.breakEndTime.minute())
        .second(0);

      intervals = intervals.flatMap((interval) => {
        if (
          breakStartTime.isSameOrAfter(interval.endTime) ||
          breakEndTime.isSameOrBefore(interval.startTime)
        ) {
          return [interval];
        }

        const newIntervals = [];

        if (breakStartTime.isAfter(interval.startTime)) {
          newIntervals.push({
            startTime: interval.startTime,
            endTime: breakStartTime,
          });
        }

        if (breakEndTime.isBefore(interval.endTime)) {
          newIntervals.push({
            startTime: breakEndTime,
            endTime: interval.endTime,
          });
        }

        return newIntervals;
      });
    });

    return intervals;
  }

  return [];
};

// Function to get the name of the application
const getApplicationName = (application) => {
  const { contestant, group, event } = application;
  const name = contestant
    ? `${contestant.first_name} ${contestant.last_name}`
    : group && group.group_name
    ? group.group_name
    : 'Unknown';
  const eventName = event.name;
  const eventStyle = event.style;
  return `${name} - ${eventName} (${eventStyle})`;
};

// Function to check for conflicts
const checkForConflict = (
  application,
  scheduledApplications,
  startTime,
  endTime
) => {
  const contestantIds = [];

  if (application.contestant) {
    contestantIds.push(application.contestant._id);
  }

  if (application.group && application.group.group_members) {
    application.group.group_members.forEach((member) => {
      const contestantId = member._id || member;
      contestantIds.push(contestantId);
    });
  }

  for (const scheduledApp of scheduledApplications) {
    const scheduledStartTime = scheduledApp.startTime;
    const scheduledEndTime = scheduledApp.endTime;
    const scheduledContestantIds = [];

    if (scheduledApp.contestant) {
      scheduledContestantIds.push(scheduledApp.contestant._id);
    }

    if (scheduledApp.group && scheduledApp.group.group_members) {
      scheduledApp.group.group_members.forEach((member) => {
        const contestantId = member._id || member;
        scheduledContestantIds.push(contestantId);
      });
    }

    const sharedContestantIds = contestantIds.filter((id) =>
      scheduledContestantIds.includes(id)
    );

    if (sharedContestantIds.length > 0) {
      if (
        startTime.isBefore(scheduledEndTime) &&
        endTime.isAfter(scheduledStartTime)
      ) {
        return true;
      }
    }
  }

  return false;
};

const isInstrumentalEvent = (application) => {
  const eventName = application?.event?.name;
  return (
    typeof eventName === 'string' &&
    eventName.trim().toLowerCase() === 'instrumental'
  );
};

const isVarietyActsEvent = (application) => {
  const eventName = application?.event?.name;
  return (
    typeof eventName === 'string' &&
    eventName.trim().toLowerCase() === 'variety acts'
  );
};

const getFirstSundayDate = (dateList = []) => {
  return (
    dateList.find(
      (date) => date && typeof date.day === 'function' && date.day() === 0
    ) || null
  );
};

const GROUP_TYPE_ORDER = [
  'duet',
  'trio',
  'small group',
  'large group',
  'production line',
];

const normalizeGroupType = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  const normalizedValue = value.trim().toLowerCase();

  if (normalizedValue === 'duet' || normalizedValue === 'duets') {
    return 'duet';
  }

  if (normalizedValue === 'trio' || normalizedValue === 'trios') {
    return 'trio';
  }

  if (
    normalizedValue === 'small group' ||
    normalizedValue === 'small groups'
  ) {
    return 'small group';
  }

  if (
    normalizedValue === 'large group' ||
    normalizedValue === 'large groups'
  ) {
    return 'large group';
  }

  if (
    normalizedValue === 'production line' ||
    normalizedValue === 'production lines'
  ) {
    return 'production line';
  }

  return normalizedValue;
};

const getGroupType = (application) => {
  const explicitGroupType = normalizeGroupType(
    application?.group_type ||
      application?.groupType ||
      application?.group?.group_type ||
      application?.group?.groupType ||
      application?.group?.type
  );

  if (GROUP_TYPE_ORDER.includes(explicitGroupType)) {
    return explicitGroupType;
  }

  const memberCount = application?.group?.group_members?.length;

  if (memberCount === 2) {
    return 'duet';
  }

  if (memberCount === 3) {
    return 'trio';
  }

  return explicitGroupType;
};

// Function to schedule applications for all venues
const scheduleApplicationsForAllVenues = (
  applicationsPerVenue,
  dateList,
  times,
  venues,
  gapDuration
) => {
  const scheduled = [];
  const unscheduled = [];
  const firstSundayDate = getFirstSundayDate(dateList);

  const allApplications = [];
  for (const venueId in applicationsPerVenue) {
    const venueApplications = applicationsPerVenue[venueId].map((app) => ({
      ...app,
      venueId,
    }));
    allApplications.push(...venueApplications);
  }

  const groupedByName = {};

  allApplications.forEach((app) => {
    const eventName = app.event.name;
    const eventStyle = app.event.style;

    if (!groupedByName[eventName]) {
      groupedByName[eventName] = {};
    }

    if (!groupedByName[eventName][eventStyle]) {
      groupedByName[eventName][eventStyle] = [];
    }

    groupedByName[eventName][eventStyle].push(app);
  });

  const sortedEventNames = Object.keys(groupedByName);
  const sortedApplications = [];

  const getAgeSortKey = (application) => {
    const ageLabel = application.group_age || application.group?.group_age || '';
    const numberMatches = ageLabel.match(/\d+/g) || [];
    const ageNumbers = numberMatches
      .map((value) => Number(value))
      .filter((value) => !Number.isNaN(value));

    if (ageNumbers.length === 0) {
      return {
        minAge: Number.MAX_SAFE_INTEGER,
        maxAge: Number.MAX_SAFE_INTEGER,
        ageLabel,
      };
    }

    if (ageNumbers.length === 1) {
      return {
        minAge: ageNumbers[0],
        maxAge: ageNumbers[0],
        ageLabel,
      };
    }

    return {
      minAge: ageNumbers[0],
      maxAge: ageNumbers[1],
      ageLabel,
    };
  };

  const compareByAge = (firstApplication, secondApplication) => {
    const firstAge = getAgeSortKey(firstApplication);
    const secondAge = getAgeSortKey(secondApplication);

    if (firstAge.minAge !== secondAge.minAge) {
      return firstAge.minAge - secondAge.minAge;
    }

    if (firstAge.maxAge !== secondAge.maxAge) {
      return firstAge.maxAge - secondAge.maxAge;
    }

    return firstAge.ageLabel.localeCompare(secondAge.ageLabel);
  };

  sortedEventNames.forEach((eventName) => {
    const eventStyles = Object.keys(groupedByName[eventName]).sort((a, b) =>
      a.localeCompare(b)
    );

    eventStyles.forEach((eventStyle) => {
      const styleApplications = groupedByName[eventName][eventStyle];

      const soloApplications = styleApplications
        .filter((application) => Boolean(application.contestant))
        .sort(compareByAge);

      const groupedApplicationsByType = GROUP_TYPE_ORDER.reduce(
        (accumulator, groupType) => {
          accumulator[groupType] = [];
          return accumulator;
        },
        {}
      );

      const otherGroupApplications = [];

      styleApplications
        .filter((application) => !application.contestant)
        .forEach((application) => {
          const groupType = getGroupType(application);

          if (GROUP_TYPE_ORDER.includes(groupType)) {
            groupedApplicationsByType[groupType].push(application);
            return;
          }

          otherGroupApplications.push(application);
        });

      const orderedGroupApplications = GROUP_TYPE_ORDER.flatMap((groupType) =>
        groupedApplicationsByType[groupType].sort(compareByAge)
      );

      sortedApplications.push(
        ...soloApplications,
        ...orderedGroupApplications,
        ...otherGroupApplications.sort(compareByAge)
      );
    });
  });

  const prioritizedApplications = [
    ...sortedApplications.filter((application) => isInstrumentalEvent(application)),
    ...sortedApplications.filter(
      (application) =>
        !isInstrumentalEvent(application) && !isVarietyActsEvent(application)
    ),
    ...sortedApplications.filter((application) => isVarietyActsEvent(application)),
  ];

  const dateTimeSlots = {};
  dateList.forEach((date) => {
    const dateStr = date.format('YYYY-MM-DD');
    dateTimeSlots[dateStr] = {};
    const timesForDate = times[dateStr];

    venues.forEach((venue) => {
      const availableIntervals = getAvailableIntervalsForDate(
        timesForDate,
        date,
        venue
      );

      if (availableIntervals.length > 0) {
        dateTimeSlots[dateStr][venue._id] = {
          intervals: availableIntervals,
          currentIntervalIndex: 0,
          currentTime: availableIntervals[0].startTime.clone(),
        };
      }
    });
  });

  const scheduledApplicationIds = new Set();

  for (let i = 0; i < prioritizedApplications.length; i++) {
    const application = prioritizedApplications[i];
    const applicationId = application._id;
    const duration = application.event.duration;
    const venueId = application.venueId;
    let scheduledFlag = false;

    if (scheduledApplicationIds.has(applicationId)) {
      continue;
    }

    const dateCandidates = isInstrumentalEvent(application)
      ? firstSundayDate
        ? [firstSundayDate]
        : []
      : dateList;

    for (const date of dateCandidates) {
      const dateStr = date.format('YYYY-MM-DD');
      const timeSlotsForDate = dateTimeSlots[dateStr];

      if (timeSlotsForDate && timeSlotsForDate[venueId]) {
        const timeSlot = timeSlotsForDate[venueId];
        const intervals = timeSlot.intervals;
        let currentIntervalIndex = timeSlot.currentIntervalIndex;
        let potentialStartTime = timeSlot.currentTime.clone();

        while (currentIntervalIndex < intervals.length) {
          const interval = intervals[currentIntervalIndex];

          if (potentialStartTime.isBefore(interval.startTime)) {
            potentialStartTime = interval.startTime.clone();
          }

          let potentialEndTime = potentialStartTime
            .clone()
            .add(duration, 'second')
            .add(gapDuration, 'second');

          while (potentialEndTime.isSameOrBefore(interval.endTime)) {
            const hasConflict = checkForConflict(
              application,
              scheduled,
              potentialStartTime,
              potentialEndTime
            );

            if (!hasConflict) {
              scheduled.push({
                ...application,
                scheduledDate: dateStr,
                startTime: potentialStartTime.clone(),
                endTime: potentialEndTime.clone().subtract(gapDuration, 'second'),
              });

              scheduledApplicationIds.add(applicationId);
              timeSlot.currentTime = potentialEndTime.clone();
              timeSlot.currentIntervalIndex = currentIntervalIndex;
              scheduledFlag = true;
              break;
            }

            potentialStartTime = potentialStartTime.clone().add(5, 'minute');

            if (potentialStartTime.isSameOrAfter(interval.endTime)) {
              break;
            }

            potentialEndTime = potentialStartTime
              .clone()
              .add(duration, 'second')
              .add(gapDuration, 'second');
          }

          if (scheduledFlag) {
            break;
          }

          currentIntervalIndex++;
          if (currentIntervalIndex < intervals.length) {
            potentialStartTime = intervals[currentIntervalIndex].startTime.clone();
          }
        }
      }

      if (scheduledFlag) {
        break;
      }
    }

    if (!scheduledFlag) {
      unscheduled.push(application);
    }
  }

  const scheduledPerVenue = {};
  const unscheduledPerVenue = {};

  scheduled.forEach((app) => {
    const venueId = app.venueId;
    if (!scheduledPerVenue[venueId]) {
      scheduledPerVenue[venueId] = [];
    }
    scheduledPerVenue[venueId].push(app);
  });

  unscheduled.forEach((app) => {
    const venueId = app.venueId;
    if (!unscheduledPerVenue[venueId]) {
      unscheduledPerVenue[venueId] = [];
    }
    unscheduledPerVenue[venueId].push(app);
  });

  return { scheduledPerVenue, unscheduledPerVenue };
};

export { scheduleApplicationsForAllVenues, estimateEndTimeForRequiredMinutes };